import { useEffect, useMemo, useState } from 'react';
import useShallowEqualSelector from './useShallowEqualSelector';
import JWT from 'jsonwebtoken';
import { getCookieValue, hashValue } from '../utils/analyticsUtils';
import { ADOBE } from '../utils/AppConstants';
/**
 * This hook is used as a generic utility to abstract away sending Adobe Analytics events for page load.
 *
 * Incase we need it later, the hook returns a helper method for pushing custom Adobe Analytics events.
 * @param {string} pageName
 * @param {string} siteSection
 */
const useAdobeAnalyticsPage = (pageName, siteSection) => {
    if (!window.adobeDataLayer) {
        window.adobeDataLayer = [];
    }
    const [hashedCIAMuuid, setHashedCIAMuuid] = useState(null);
    const { jwt, CIAMuuid, group } = useShallowEqualSelector((state) => ({
        jwt: state.Authentication.jwt,
        CIAMuuid: state.Authentication.ciamUuid,
        group: state.Authentication.group,
    }));
    const language = useMemo(() => (jwt ? JWT.decode(jwt).Identity.language : null), [jwt]);
    const userId = useMemo(() => {
        if (jwt) {
            const jwtData = JWT.decode(jwt);
            return `${jwtData.Identity.clientNumber}-${jwtData.Identity.certificate}`;
        }
        return null;
    }, [jwt]);
    const pageData = useMemo(
        () => ({
            botDetector: window.navigator.webdriver,
            brand: 'Manulife',
            country: 'CA',
            domain: window.location.hostname,
            language: language,
            loB: 'Manage Benefits',
            pageName: `Member-profile:${pageName}`,
            pageNavPosition: document.documentElement.scrollTop || document.body.scrollTop,
            pageURL: window.location.href,
            path: window.location.pathname,
            previousPage: getCookieValue('PreviousPageURL'),
            segment: 'CA',
            siteSection: siteSection,
            siteType: 'CWS',
            title: document.title,
            type: 'Transactional',
            userAgent: window.navigator.userAgent,
        }),
        [language, pageName, siteSection]
    );
    const userData = useMemo(
        () => ({
            type: 'Customer',
            primaryCustomerID: hashedCIAMuuid,
            secondaryCustomerID: group,
            tertiaryCustomerID: JWT.decode(jwt)?.Identity.certificate,
            anonymousID:
                window._satellite && window._satellite.getVisitorId()
                    ? window._satellite.getVisitorId().getMarketingCloudVisitorID()
                    : null,
            medalliaUUID: sessionStorage.getItem('Feedback_UUID') || null,
        }),
        [jwt, hashedCIAMuuid, group]
    );

    useEffect(() => {
        const handleHashValue = async () => {
            if (CIAMuuid) {
                const result = await hashValue(CIAMuuid);
                setHashedCIAMuuid(result);
            } else {
                setHashedCIAMuuid(null);
            }
        };
        handleHashValue();
    }, [CIAMuuid]);

    // Send a page load event to Adobe Analytics
    useEffect(() => {
        if (userId && (!CIAMuuid || hashedCIAMuuid)) {
            window.adobeDataLayer.push({
                event: ADOBE.PAGE_LOAD,
                page: pageData,
                user: userData,
            });
        }
    }, [userId, hashedCIAMuuid, pageData, userData]);

    /**
     * Provide a wrapper around sending Adobe Analytics events that a page can use
     * @param {object} eventObject
     */
    const sendAdobeAnalyticsEvent = (eventObject) => {
        window.CWC_MLCA.dataLayer.push(eventObject);
    };

    /**
     * This method is responsible for pushing a transaction event to Adobe DataLayer
     * @param {string} eventType variable event
     * @param {string} transactionName one of {transaction} properties
     */
    const sendAdobeAnalyticsTransaction = (eventType, transactionName) => {
        window.adobeDataLayer.push({
            event: eventType,
            transaction: {
                transactionName,
                transactionCategory: 'Member Profile',
            },
        });
    };

    /**
     * Provide a wrapper around sending Adobe Analytics error events that a page can use. Should provide the actual
     * error code and message from any failed backend calls.
     * @param {string} errorCode
     * @param {string} errorType
     * @param {string} errorDescription
     */
    const sendAdobeAnalyticsError = (errorCode, errorType, errorDescription) => {
        window.adobeDataLayer.push({
            event: ADOBE.TRANSACTION_ERROR,
            transaction: {
                transactionName: 'Edit My Personal Information',
                transactionCategory: 'Member Profile',
            },
            error: {
                errorCode,
                errorType,
                errorDescription,
                errorCategory: 'profile_error',
            },
        });
    };

    return {
        sendAdobeAnalyticsEvent,
        sendAdobeAnalyticsError,
        sendAdobeAnalyticsTransaction,
    };
};

export default useAdobeAnalyticsPage;
